import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { format, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsvpStatus, setRsvpStatus] = useState(new Map());

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  const fetchUpcomingEvents = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase?.from('industry_events')?.select(`
          id,
          title,
          description,
          event_type,
          location,
          start_date,
          end_date,
          attendee_count,
          creator:user_profiles!industry_events_creator_id_fkey (
            full_name,
            partners (
              business_name,
              partner_type
            )
          )
        `)?.eq('is_public', true)?.gte('start_date', new Date()?.toISOString())?.order('start_date', { ascending: true })?.limit(5);

      if (error) throw error;

      setEvents(data || []);

      // Fetch user RSVPs for these events
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user && data?.length > 0) {
        const eventIds = data?.map(event => event?.id);
        const { data: rsvps } = await supabase?.from('event_rsvps')?.select('event_id, rsvp_status')?.eq('user_id', session?.user?.id)?.in('event_id', eventIds);

        if (rsvps) {
          const rsvpMap = new Map();
          rsvps?.forEach(rsvp => {
            rsvpMap?.set(rsvp?.event_id, rsvp?.rsvp_status);
          });
          setRsvpStatus(rsvpMap);
        }
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) return;

      const { error } = await supabase?.from('event_rsvps')?.upsert({
          event_id: eventId,
          user_id: session?.user?.id,
          rsvp_status: status
        }, {
          onConflict: 'event_id,user_id'
        });

      if (error) throw error;

      // Update local state
      setRsvpStatus(prev => new Map(prev.set(eventId, status)));

      // Update attendee count in the events list
      if (status === 'going') {
        setEvents(prev => prev?.map(event => 
          event?.id === eventId 
            ? { ...event, attendee_count: (event?.attendee_count || 0) + 1 }
            : event
        ));
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
    }
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'performance': return 'Music';
      case 'workshop': return 'BookOpen';
      case 'networking': return 'Users';
      case 'conference': return 'Mic';
      default: return 'Calendar';
    }
  };

  const getEventTypeLabel = (eventType) => {
    const labels = {
      'performance': 'Performance',
      'workshop': 'Atelier',
      'networking': 'Networking',
      'conference': 'Conférence',
      'festival': 'Festival'
    };
    return labels?.[eventType] || 'Événement';
  };

  const formatEventDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      
      if (isSameDay(date, now)) {
        return `Aujourd'hui ${format(date, 'HH:mm')}`;
      } else {
        return format(date, 'dd MMM • HH:mm', { locale: fr });
      }
    } catch {
      return 'Date à confirmer';
    }
  };

  const getRSVPButton = (event) => {
    const currentStatus = rsvpStatus?.get(event?.id);
    
    if (currentStatus === 'going') {
      return (
        <Button
          size="xs"
          variant="outline"
          className="text-green-600 border-green-200 hover:bg-green-50"
          onClick={() => handleRSVP(event?.id, 'not_going')}
          iconName="Check"
        >
          J'y vais
        </Button>
      );
    }
    
    return (
      <Button
        size="xs"
        variant="outline"
        onClick={() => handleRSVP(event?.id, 'going')}
        iconName="Calendar"
      >
        Participer
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Événements à venir</h3>
        <div className="space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="animate-pulse">
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Événements à venir</h3>
        <Icon name="Calendar" size={20} className="text-muted-foreground" />
      </div>

      {events?.length > 0 ? (
        <div className="space-y-4">
          {events?.map((event) => (
            <div
              key={event?.id}
              className="group cursor-pointer hover:bg-muted rounded-lg p-3 transition-colors -mx-3"
            >
              {/* Event Header */}
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  event?.event_type === 'performance' ? 'bg-purple-100 text-purple-600' :
                  event?.event_type === 'workshop' ? 'bg-blue-100 text-blue-600' :
                  event?.event_type === 'networking'? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'
                }`}>
                  <Icon name={getEventTypeIcon(event?.event_type)} size={16} />
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground group-hover:text-accent transition-colors line-clamp-2 text-sm">
                    {event?.title}
                  </h4>
                  
                  <div className="flex items-center space-x-2 mt-1 text-xs text-muted-foreground">
                    <span>{getEventTypeLabel(event?.event_type)}</span>
                    <span>•</span>
                    <span>{formatEventDate(event?.start_date)}</span>
                  </div>

                  {event?.location && (
                    <div className="flex items-center space-x-1 mt-1 text-xs text-muted-foreground">
                      <Icon name="MapPin" size={10} />
                      <span className="truncate">{event?.location}</span>
                    </div>
                  )}

                  {/* Event Creator */}
                  {event?.creator && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Par {event?.creator?.partners?.[0]?.business_name || event?.creator?.full_name}
                    </div>
                  )}

                  {/* Attendees & RSVP */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <Icon name="Users" size={12} />
                      <span>{event?.attendee_count || 0} participants</span>
                    </div>
                    
                    {getRSVPButton(event)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Calendar" size={24} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Aucun événement à venir</p>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-border">
        <Button variant="outline" size="sm" className="w-full" iconName="Plus">
          Créer un événement
        </Button>
      </div>
    </div>
  );
};

export default UpcomingEvents;